// Invincible Shield Effect (Epic Holy Golden Aura)
// Features: Forging geometric runes -> Manifestation of an epic Aegis Shield with a rotating Runic Halo -> Sustained holy resonance -> Dissipation via light beams.

const CONFIG = {
    scale: 0.9,
    forgingDuration: 600,
    manifestationDuration: 400,
    resonanceDuration: 1500, // Time it stays fully active
    dissipationDuration: 500,
    hoverAmplitude: 6,
    hoverSpeed: 3,
    haloRotationSpeed: 0.5, // radians per second
    lightMoteCount: 20
};

// ==========================================
// HIGH-FIDELITY EPIC SVG PATHS
// ==========================================

// --- The Aegis Shield ---
// A towering, kite-style golden shield
const aegisOuterBorder = new Path2D("M0,-80 C30,-75 50,-60 60,-20 C70,30 40,80 0,110 C-40,80 -70,30 -60,-20 C-50,-60 -30,-75 0,-80 Z");
const aegisInnerPanel = new Path2D("M0,-65 C20,-60 35,-48 45,-15 C52,25 28,68 0,90 C-28,68 -52,25 -45,-15 C-35,-48 -20,-60 0,-65 Z");

// Decorative raised gold trims and religious/holy motifs inside the shield
const aegisTrimLeft = new Path2D("M0,-80 C-30,-75 -50,-60 -60,-20 C-70,30 -40,80 0,110 M0,-65 C-20,-60 -35,-48 -45,-15 C-52,25 -28,68 0,90");
const aegisTrimRight = new Path2D("M0,-80 C30,-75 50,-60 60,-20 C70,30 40,80 0,110 M0,-65 C20,-60 35,-48 45,-15 C52,25 28,68 0,90");

// Central intricate core wrapping the gem
const aegisCore = new Path2D("M0,-30 L15,-10 L10,10 L0,25 L-10,10 L-15,-10 Z M0,-15 L8,-2 L5,8 L0,15 L-5,8 L-8,-2 Z");
const aegisGem = new Path2D("M0,-5 L5,0 L0,8 L-5,0 Z");

// --- The Runic Halo ---
// Complex geometric halo rotating behind the shield
const haloRingOuter = new Path2D("M0,-110 A110,110 0 1,1 0,110 A110,110 0 1,1 0,-110 M0,-100 A100,100 0 1,0 0,100 A100,100 0 1,0 0,-100");
const haloRingInner = new Path2D("M0,-75 A75,75 0 1,1 0,75 A75,75 0 1,1 0,-75 M0,-82 A82,82 0 1,0 0,82 A82,82 0 1,0 0,-82");

// We define cardinal nodes that stick out of the halo
const haloNodeTop = new Path2D("M0,-100 L10,-120 L0,-140 L-10,-120 Z");
const haloNodeBot = new Path2D("M0,100 L10,120 L0,140 L-10,120 Z");
const haloNodeRight = new Path2D("M100,0 L120,10 L140,0 L120,-10 Z");
const haloNodeLeft = new Path2D("M-100,0 L-120,10 L-140,0 L-120,-10 Z");


// ==========================================
// PARTICLES
// ==========================================

class LightMote {
    x: number; y: number; vy: number;
    life: number; maxLife: number;
    size: number;
    wobblePhase: number; wobbleSpeed: number; wobbleAmplitude: number;

    constructor(baseX: number, baseY: number) {
        // Spawn around the bottom of the shield
        this.x = baseX + (Math.random() - 0.5) * 80;
        this.y = baseY + 80 + Math.random() * 40;
        this.vy = -(1 + Math.random() * 2);

        this.maxLife = 50 + Math.random() * 60;
        this.life = this.maxLife;
        this.size = 1.5 + Math.random() * 2;

        this.wobblePhase = Math.random() * Math.PI * 2;
        this.wobbleSpeed = 0.05 + Math.random() * 0.05;
        this.wobbleAmplitude = 0.5 + Math.random() * 1.5;
    }

    update() {
        this.y += this.vy;
        this.x += Math.sin(this.wobblePhase) * this.wobbleAmplitude;
        this.wobblePhase += this.wobbleSpeed;
        this.life--;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;

        ctx.save();
        const opacity = Math.sin((this.life / this.maxLife) * Math.PI); // Fade in and out
        ctx.globalAlpha = opacity;

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = '#ffcc00';
        ctx.shadowBlur = 10;
        ctx.fill();

        // Inner core
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        ctx.restore();
    }
}

// ==========================================
// EFFECT STATE & RENDERER
// ==========================================

export type InvincibleEffect = {
    kind: 'invincible'
    t: number
    tx: number; ty: number
    data?: {
        phase: 'FORGING' | 'MANIFESTATION' | 'RESONANCE' | 'DISSIPATION'
        motes: LightMote[]
        runesBuilt: number // 0.0 to 1.0 for forging arcs
    }
}

export const createInvincibleEffect = (tx: number, ty: number): InvincibleEffect => ({
    kind: 'invincible', t: 0, tx, ty
});

export const renderInvincibleEffect = (
    eff: InvincibleEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    if (!eff.data) {
        eff.data = {
            phase: 'FORGING',
            motes: [],
            runesBuilt: 0
        };
    }

    const d = eff.data;
    const totalTime = CONFIG.forgingDuration + CONFIG.manifestationDuration + CONFIG.resonanceDuration + CONFIG.dissipationDuration;

    // Timeline logic
    if (eff.t < CONFIG.forgingDuration) {
        d.phase = 'FORGING';
        d.runesBuilt = eff.t / CONFIG.forgingDuration; // 0 to 1
    } else if (eff.t < CONFIG.forgingDuration + CONFIG.manifestationDuration) {
        d.phase = 'MANIFESTATION';
        // Spawn motes continuously once manifested
        if (Math.random() < 0.4) d.motes.push(new LightMote(eff.tx, eff.ty));
    } else if (eff.t < totalTime - CONFIG.dissipationDuration) {
        d.phase = 'RESONANCE';
        if (Math.random() < 0.3) d.motes.push(new LightMote(eff.tx, eff.ty));
    } else {
        d.phase = 'DISSIPATION';
    }

    ctx.save();
    ctx.translate(eff.tx, eff.ty);

    // ==========================================
    // RENDER: THE RUNIC HALO (Background)
    // ==========================================
    ctx.save();

    // Scale up during forging/manifesting
    let haloScale = 0;
    let haloAlpha = 0;

    if (d.phase === 'FORGING') {
        const ease = 1 - Math.pow(1 - d.runesBuilt, 3); // Cubic ease out
        haloScale = CONFIG.scale * ease;
        haloAlpha = ease;
    } else if (d.phase === 'DISSIPATION') {
        const remaining = (totalTime - eff.t) / CONFIG.dissipationDuration;
        haloScale = CONFIG.scale * Math.max(0, remaining);
        haloAlpha = Math.max(0, remaining);
    } else {
        haloScale = CONFIG.scale;
        haloAlpha = 1.0;
    }

    ctx.scale(haloScale, haloScale);

    // Epic Paladin Background Aura Glow
    if (haloAlpha > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.globalAlpha = haloAlpha * 0.7; // Bright ambient holy light
        const auraGrad = ctx.createRadialGradient(0, 0, 20, 0, 0, 220);
        auraGrad.addColorStop(0, 'rgba(255, 255, 255, 1)');
        auraGrad.addColorStop(0.3, 'rgba(255, 220, 100, 0.6)');
        auraGrad.addColorStop(1, 'rgba(255, 180, 0, 0)');
        ctx.fillStyle = auraGrad;
        ctx.beginPath();
        ctx.arc(0, 0, 220, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }

    // Continuous rotation
    const rotation = (eff.t / 1000) * CONFIG.haloRotationSpeed;
    ctx.rotate(rotation);

    if (haloAlpha > 0) {
        ctx.globalAlpha = haloAlpha;

        // Increased glow for the halo itself
        ctx.shadowColor = '#ffea88';
        ctx.shadowBlur = 35;

        // Draw the outer ring and nodes
        ctx.fillStyle = 'rgba(255, 230, 150, 0.9)';
        ctx.fill(haloRingOuter);
        ctx.fill(haloNodeTop);
        ctx.fill(haloNodeBot);
        ctx.fill(haloNodeLeft);
        ctx.fill(haloNodeRight);

        // Draw the inner segments
        // We simulate a segmented inner ring using a dashed stroke
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 6;
        ctx.setLineDash([20, 15, 40, 15]); // Pattern of solid/gap
        ctx.stroke(haloRingInner);
        ctx.setLineDash([]); // Reset

        ctx.shadowBlur = 0;
    }

    ctx.restore();

    // ==========================================
    // RENDER: THE AEGIS SHIELD (Foreground)
    // ==========================================
    ctx.save();

    let shieldScale = 0;
    let shieldAlpha = 0;

    if (d.phase === 'FORGING') {
        // Shield doesn't exist yet, just building the aura
    } else if (d.phase === 'MANIFESTATION') {
        const progress = (eff.t - CONFIG.forgingDuration) / CONFIG.manifestationDuration;
        // Elastic/overshoot ease for the heavy shield slamming into existence
        const c4 = (2 * Math.PI) / 3;
        shieldScale = CONFIG.scale * (progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress) * Math.sin((progress * 10 - 0.75) * c4));
        shieldAlpha = Math.min(1, progress * 2);
    } else if (d.phase === 'DISSIPATION') {
        const remaining = (totalTime - eff.t) / CONFIG.dissipationDuration;
        // Shield shrinks and fades
        shieldScale = CONFIG.scale * (0.8 + 0.2 * remaining);
        shieldAlpha = Math.max(0, remaining);
    } else {
        shieldScale = CONFIG.scale;
        shieldAlpha = 1.0;
    }

    if (shieldAlpha > 0) {
        // Hovering effect
        const hoverOffset = Math.sin((eff.t / 1000) * CONFIG.hoverSpeed) * CONFIG.hoverAmplitude;
        ctx.translate(0, hoverOffset);
        ctx.scale(shieldScale, shieldScale);
        ctx.globalAlpha = shieldAlpha;

        // 0. Epic Holy Shield Aura (Outer Glow)
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.shadowColor = '#ffdf70';
        ctx.shadowBlur = 60;
        ctx.fillStyle = 'rgba(255, 230, 100, 0.2)';
        ctx.fill(aegisOuterBorder);
        ctx.fill(aegisOuterBorder); // Double fill to intensify the optical bloom
        ctx.restore();

        // 1. Shield Base (Dark brushed gold)
        const baseGrad = ctx.createLinearGradient(-60, -80, 60, 110);
        baseGrad.addColorStop(0, '#85661b');
        baseGrad.addColorStop(0.5, '#2e250f');
        baseGrad.addColorStop(1, '#664d14');
        ctx.fillStyle = baseGrad;

        ctx.shadowColor = 'rgba(0,0,0,0.8)';
        ctx.shadowBlur = 15;
        ctx.shadowOffsetY = 10;
        ctx.fill(aegisOuterBorder);
        ctx.shadowColor = 'transparent';

        // 2. Bright Golden Trim (Outer)
        ctx.strokeStyle = '#fad678';
        ctx.lineWidth = 4;
        ctx.stroke(aegisOuterBorder);

        // Inner shading drop shadow for the rim
        ctx.strokeStyle = '#473611';
        ctx.lineWidth = 2;
        ctx.stroke(aegisOuterBorder); // Strokings inside slightly requires clip, but a double stroke fakes it 

        // 3. Inner Panel (Darker)
        const innerGrad = ctx.createLinearGradient(0, -65, 0, 90);
        innerGrad.addColorStop(0, '#1c1508');
        innerGrad.addColorStop(1, '#3b2f15');
        ctx.fillStyle = innerGrad;
        ctx.fill(aegisInnerPanel);

        // Bright Inner Trim
        ctx.strokeStyle = '#e6b84e';
        ctx.lineWidth = 2;
        ctx.stroke(aegisInnerPanel);

        // 4. Intricate Core
        ctx.fillStyle = '#6e541b';
        ctx.fill(aegisCore);
        ctx.strokeStyle = '#fed46b';
        ctx.lineWidth = 1.5;
        ctx.stroke(aegisCore);

        // 5. Central Gem (Pulsing Sapphire)
        const pulse = 0.8 + Math.sin(eff.t * 0.005) * 0.2;

        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 15 * pulse;

        const gemGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 8);
        gemGrad.addColorStop(0, `#ffffff`);
        gemGrad.addColorStop(0.5, `rgba(0, 200, 255, ${pulse})`);
        gemGrad.addColorStop(1, `#004488`);

        ctx.fillStyle = gemGrad;
        ctx.fill(aegisGem);
        ctx.shadowBlur = 0;

        // Gem highlight
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = 1;
        ctx.stroke(aegisGem);

        // 6. Dissipation Light Beams (Only in final phase)
        if (d.phase === 'DISSIPATION') {
            const remaining = (totalTime - eff.t) / CONFIG.dissipationDuration;
            ctx.globalCompositeOperation = 'screen';
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(-40, -300);
            ctx.lineTo(40, -300);
            ctx.closePath();

            const beamGrad = ctx.createLinearGradient(0, 0, 0, -300);
            beamGrad.addColorStop(0, `rgba(255, 230, 200, ${remaining})`);
            beamGrad.addColorStop(1, 'rgba(255, 200, 100, 0)');

            ctx.fillStyle = beamGrad;
            ctx.fill();
            ctx.globalCompositeOperation = 'source-over';
        }
    }

    ctx.restore(); // Restore to entity center (tx, ty)

    // ==========================================
    // RENDER: LIGHT MOTES (Holy Particles)
    // ==========================================
    // Rendered outside the scale/rotate context so they drift freely
    ctx.restore(); // Restore to Global canvas space

    for (let i = d.motes.length - 1; i >= 0; i--) {
        const mote = d.motes[i];
        mote.update();
        mote.draw(ctx);
        if (mote.life <= 0) {
            d.motes.splice(i, 1);
        }
    }

    // Effect ends when time is up AND particles are gone
    if (eff.t > totalTime && d.motes.length === 0) {
        return false;
    }

    return true;
};
